<template>
    <div>
        <div class="p-6 pb-0">
            <BreadCrumbs :navigation="navigation" />
        </div>

        <div class="p-6">
            <div class="bg-white shadow-md rounded-lg p-6 border border-gray-200 w-full mx-auto">
                <div class="flex justify-between items-center mb-6 border-b pb-4">
                    <h1 class="text-2xl font-bold text-gray-800">Resoconto Ordini di Produzione</h1>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6 items-end">
                    <div class="flex flex-col">
                        <label class="text-sm font-semibold text-gray-700 mb-1">N° Ordine di Produzione</label>
                        <input
                            type="text"
                            v-model="filters.mono"
                            @change="loadData()"
                            placeholder="Cerca per Codice OdP..."
                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border"
                        >
                    </div>

                    <div class="flex flex-col">
                        <label class="text-sm font-semibold text-gray-700 mb-1">Commessa</label>
                        <div class="relative w-full inline-flex items-center cursor-pointer" @click="loadJobs()">
                            <span class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2.5 border bg-white truncate">
                                {{ filters.job ? filters.job : 'Tutte le commesse' }}
                            </span>
                            <span class="absolute right-3 text-gray-500 pointer-events-none">
                            </span>
                        </div>
                    </div>

                    <div class="flex flex-col">
                        <label class="text-sm font-semibold text-gray-700 mb-1">Piano di Produzione</label>
                        <div class="relative w-full inline-flex items-center cursor-pointer" @click="loadPlans()">
                            <span class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2.5 border bg-white truncate">
                                {{ filters.productionPlanNo ? filters.productionPlanNo : 'Tutti i piani' }}
                            </span>
                            <span class="absolute right-3 text-gray-500 pointer-events-none">
                            </span>
                        </div>
                    </div>

                    <div class="flex flex-col">
                        <label class="text-sm font-semibold text-gray-700 mb-1">Consegna da</label>
                        <input
                            type="date"
                            v-model="filters.deliveryDateFrom"
                            @change="loadData()"
                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border"
                        >
                    </div>

                    <div class="flex flex-col">
                        <label class="text-sm font-semibold text-gray-700 mb-1">Consegna a</label>
                        <input
                            type="date"
                            v-model="filters.deliveryDateTo"
                            @change="loadData()"
                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border"
                        >
                    </div>
                </div>

                <div class="flex flex-wrap items-center gap-x-6 gap-y-2 mb-6">
                    <label
                        v-for="status in moStatuses"
                        :key="status.value"
                        class="inline-flex items-center gap-2 cursor-pointer"
                    >
                        <input
                            v-model="filters.statuses"
                            type="checkbox"
                            :value="status.value"
                            @change="loadData()"
                            class="rounded border-gray-300 text-blue-600 shadow-sm focus:ring-blue-500"
                        >
                        <span
                            class="inline-block px-2 py-0.5 rounded text-xs font-semibold"
                            :class="moStatusClass(status.value)"
                        >
                            {{ status.label }}
                        </span>
                    </label>
                </div>

                <div v-if="hasCreatedRows || selectedMos.length" class="mb-3 flex justify-end">
                    <button
                        type="button"
                        @click="launchSelectedMos"
                        :disabled="launchingSelectedMos || selectedMos.length === 0"
                        class="inline-flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <span v-if="launchingSelectedMos" class="inline-block h-4 w-4 animate-spin rounded-full border-b-2 border-white"></span>
                        {{ launchingSelectedMos ? `Lancio in corso (${launchProgress.completed}/${launchProgress.total})...` : `Lancia in Produzione selezionati (${selectedMos.length})` }}
                    </button>
                </div>
                <div class="overflow-x-auto">
                    <PageServerTable
                        :headers="tableProps.headers"
                        :items="tableProps.items"
                        :items-length="tableProps.total"
                        :items-per-page="tableProps.ipp"
                        :items-per-page-options="tableProps.ippo"
                        :page="tableProps.page"
                        :sort-by="tableProps.sortby"
                        :reload-fn="loadData"
                        :loading="loading"
                        hover
                    >
                        <template #header.LaunchSelected>
                            <input
                                v-if="createdRows.length"
                                type="checkbox"
                                :checked="allCreatedRowsSelected"
                                :indeterminate="someCreatedRowsSelected && !allCreatedRowsSelected"
                                :disabled="launchingSelectedMos"
                                aria-label="Seleziona o deseleziona tutti gli ODP creati nella pagina"
                                class="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:cursor-not-allowed"
                                @click.stop
                                @change="toggleAllCreatedRows($event.target.checked)"
                            >
                        </template>
                        <template #item.LaunchSelected="{ item }">
                            <input
                                v-if="item.MOStatus == MagoEnums.Production_progress.Created"
                                type="checkbox"
                                :checked="selectedMos.includes(String(item.MONo))"
                                :disabled="launchingSelectedMos"
                                :aria-label="'Seleziona ODP ' + item.MONo + ' per il lancio'"
                                class="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:cursor-not-allowed"
                                @click.stop
                                @change="toggleMoSelection(item.MONo, $event.target.checked)"
                            >
                        </template>
                        <template #item.MA_MO.MONo="{ item }">
                            <div class="flex items-center justify-end gap-1 whitespace-nowrap">
                                <span>{{ item.MONo }}</span>
                                <Link
                                    class="inline-flex items-center justify-center w-8 h-8 rounded text-blue-600 hover:bg-blue-100"               
                                    :href="route('modules.gp.prelievo', { mono: item.MONo })"
                                    v-if="canOpenPrelievo"
                                >
                                    <v-icon name="fa-dolly" class="mr-1"  title="Apri buono di prelievo" />
                                </Link>
                                <Link
                                    class="inline-flex items-center justify-center w-8 h-8 rounded text-orange-600 hover:bg-orange-100"
                                    :href="route('modules.gp.consuntiva-produzione', { mono: item.MONo })"
                                    v-if="canOpenConsuntiva"
                                >
                                    <v-icon name="md-precisionmanufacturing-round" class="mr-1" title="Apri consuntivazione" />
                                </Link>
                            </div>
                        </template>
                        <template #item.MA_MO.MOStatus="{ item }">
                            <span
                                class="inline-block px-2 py-0.5 rounded text-xs font-semibold"
                                :class="moStatusClass(item.MOStatus)"
                            >
                                {{ moStatusLabel(item.MOStatus) }}
                            </span>
                        </template>
                        <template #item.MA_MO.TBCreated="{ item }">
                            <span>{{ formatDate(item.TBCreated) }}</span>
                        </template>
                        <template #item.MA_MO.ProducedQty="{ item }">
                            <div class="flex flex-col items-end gap-1">
                                <span class="text-xs w-16 text-right font-semibold" :class="productionProgressPercent(item) > 100 ? 'text-red-600' : 'text-gray-500'">
                                    {{ productionProgressPercent(item) }}%
                                </span>
                                <div class="w-16 bg-gray-200 rounded-full h-1.5">
                                    <div
                                        class="h-1.5 rounded-full transition-all"
                                        :class="productionProgressClass(item)"
                                        :style="{ width: Math.min(100, productionProgressPercent(item)) + '%' }"
                                    ></div>
                                </div>
                                <span class="text-xs text-gray-500 whitespace-nowrap">{{ item.ProducedQty }} / {{ item.ProductionQty }} {{ item.UoM }}</span>
                            </div>
                        </template>
                        <template #item.ProductionProgress="{ item }">
                            <div class="flex flex-col items-end gap-1">
                                <span class="text-xs w-16 text-right font-semibold" :class="stepsProgressPercent(item) > 100 ? 'text-red-600' : 'text-gray-500'">
                                    {{ stepsProgressPercent(item) }}%
                                </span>
                                <div class="w-16 bg-gray-200 rounded-full h-1.5">
                                    <div
                                        class="h-1.5 rounded-full transition-all"
                                        :class="stepsProgressClass(item)"
                                        :style="{ width: Math.min(100, stepsProgressPercent(item)) + '%' }"
                                    ></div>
                                </div>
                            </div>
                        </template>
                        <template #item.MA_MO.DeliveryDate="{ item }">
                            <span>{{ formatDate(item.DeliveryDate) }}</span>
                        </template>
                        <template #no-data>
                            <div class="p-8 text-center text-gray-500">
                                Nessun ordine trovato per i filtri selezionati
                            </div>
                        </template>
                    </PageServerTable>
                </div>
            </div>
        </div>

        <!-- Job Selection Modal -->
        <div v-if="showJobTable" class="fixed inset-0 flex items-center z-50 justify-center">
            <div class="absolute inset-0 bg-black opacity-50" @click="showJobTable = false"></div>
            <div class="bg-white rounded-lg shadow-xl z-10 max-h-[80vh] overflow-y-auto w-11/12">
                <div class="px-6 py-4 border-b flex items-center justify-between bg-gray-50">
                    <h2 class="text-xl font-bold text-gray-800 flex items-center gap-2">
                        Seleziona commessa
                    </h2>
                    <button @click="showJobTable = false" class="text-gray-500 hover:text-gray-700">
                    </button>
                </div>
                <div class="p-6 pt-3">
                    <div class="mb-4">
                        <button @click="selectJob({ Job: '' })" class="text-blue-600 hover:underline text-sm">
                            Tutte le commesse
                        </button>
                    </div>
                    <PageServerTable
                        :headers="jobTableProps.headers"
                        :items="jobTableProps.items"
                        :items-length="jobTableProps.total"
                        :items-per-page="jobTableProps.ipp"
                        :items-per-page-options="jobTableProps.ippo"
                        :page="jobTableProps.page"
                        :sort-by="jobTableProps.sortby"
                        :reload-fn="loadJobs"
                        hover
                        @click:row="(e, { item }) => selectJob(item)"
                    />
                </div>
            </div>
        </div>

        <!-- Production Plan Selection Modal -->
        <div v-if="showPlanTable" class="fixed inset-0 flex items-center z-50 justify-center">
            <div class="absolute inset-0 bg-black opacity-50" @click="showPlanTable = false"></div>
            <div class="bg-white rounded-lg shadow-xl z-10 max-h-[80vh] overflow-y-auto w-11/12">
                <div class="px-6 py-4 border-b flex items-center justify-between bg-gray-50">
                    <h2 class="text-xl font-bold text-gray-800 flex items-center gap-2">
                        Seleziona piano di produzione
                    </h2>
                    <button @click="showPlanTable = false" class="text-gray-500 hover:text-gray-700">
                    </button>
                </div>
                <div class="p-6 pt-3">
                    <div class="mb-4">
                        <button @click="selectPlan({ ProductionPlanNo: '' })" class="text-blue-600 hover:underline text-sm">
                            Tutti i piani
                        </button>
                    </div>
                    <PageServerTable
                        :headers="planTableProps.headers"
                        :items="planTableProps.items"
                        :items-length="planTableProps.total"
                        :items-per-page="planTableProps.ipp"
                        :items-per-page-options="planTableProps.ippo"
                        :page="planTableProps.page"
                        :sort-by="planTableProps.sortby"
                        :reload-fn="loadPlans"
                        hover
                        @click:row="(e, { item }) => selectPlan(item)"
                    >
                        <template #item.CreationDate="{ item }">
                            {{ formatDate(item.CreationDate) }}
                        </template>
                    </PageServerTable>
                </div>
            </div>
        </div>

        <ToastNotification
            :show="toast.show"
            :message="toast.message"
            :type="toast.type"
            @update:show="toast.show = $event"
        />
    </div>
</template>

<script>
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import PageServerTable from '@/Components/PageServerTable.vue';
import ToastNotification from '@/Components/ToastNotification.vue';
import axios from 'axios';
import { Link } from '@inertiajs/vue3';

export default {
    components: {
        BreadCrumbs,
        PageServerTable,
        ToastNotification,
        Link
    },
    props: {
        canOpenPrelievo: {
            type: Boolean,
            default: false,
        },
        canOpenConsuntiva: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: false,
            selectedMos: [],
            launchingSelectedMos: false,
            launchProgress: { completed: 0, total: 0 },
            showJobTable: false,
            showPlanTable: false,
            moStatuses: [
                { value: this.MagoEnums.Production_progress.Created, label: 'Creato' },
                { value: this.MagoEnums.Production_progress.Released, label: 'Lanciato' },
                { value: this.MagoEnums.Production_progress.Processing, label: 'In lavorazione' },
                { value: this.MagoEnums.Production_progress.Confirmed, label: 'Terminato' },
                { value: this.MagoEnums.Production_progress.Proposed_by_MRP, label: 'Proposto da MRP' },
                { value: this.MagoEnums.Production_progress.Planned_by_CRP, label: 'Pianificato da CRP' },
                { value: this.MagoEnums.Production_progress.Proposed_by_MRPMinusCRP, label: 'Proposto MRP-CRP' },
            ],
            filters: {
                mono: '',
                job: '',
                productionPlanNo: '',
                deliveryDateFrom: '',
                deliveryDateTo: '',
                statuses: [
                    this.MagoEnums.Production_progress.Released,
                    this.MagoEnums.Production_progress.Processing,
                    this.MagoEnums.Production_progress.Created,
                ],
            },
            tableProps: {
                headers: [
                    { title: '',                     key: 'LaunchSelected',                       value: 'LaunchSelected',   align: 'center', sortable: false },
                    { title: 'N° ODP',               key: 'MA_MO.MONo',                          value: 'MONo',             align: 'start', sortable: true },
                    { title: 'Distinta Base',        key: 'MA_MO.BOM',                           value: 'BOM',              align: 'start', sortable: true },
                    { title: 'Commessa',             key: 'MA_MO.Job',                           value: 'Job',              align: 'start', sortable: true },
                    { title: 'Cliente',              key: 'MA_CustSupp.CompanyName',             value: 'CompanyName',      align: 'start', sortable: true },
                    { title: 'Piano di Produzione',  key: 'MA_ProductionPlans.ProductionPlanNo', value: 'ProductionPlanNo', align: 'start', sortable: true },
                    { title: 'Carico prod. fin.',     key: 'MA_MO.ProducedQty',                   value: 'ProducedQty',         align: 'end',   sortable: true },
                    { title: 'Avanzamento fasi',     key: 'ProductionProgress',                   value: 'ProductionProgress',  align: 'end',   sortable: false },
                    { title: 'Data Consegna',        key: 'MA_MO.DeliveryDate',                  value: 'DeliveryDate',        align: 'start', sortable: true },
                    { title: 'Stato',                key: 'MA_MO.MOStatus',                      value: 'MOStatus',         align: 'start', sortable: true },
                    //{ title: 'Creato il',            key: 'MA_MO.TBCreated',                     value: 'TBCreated',        align: 'start', sortable: true },
                ],
                items: [],
                total: 0,
                ipp: 25,
                ippo: [
                    { value: 10,  title: '10' },
                    { value: 25,  title: '25' },
                    { value: 50,  title: '50' },
                ],
                page: 1,
                sortby: [],
            },
            jobTableProps: {
                headers: [
                    { title: 'Commessa',        align: 'start', value: 'Job',         key: 'MA_Jobs.Job',             filterable: true, filtervalue: '' },
                    { title: 'Descrizione',     align: 'start', value: 'Description', key: 'MA_Jobs.Description',     filterable: true, filtervalue: '' },
                    { title: 'Ragione Sociale', align: 'start', value: 'RagSoc',      key: 'MA_CustSupp.CompanyName', filterable: true, filtervalue: '' },
                ],
                items: [],
                total: 0,
                ipp: 10,
                ippo: [
                    { value: 10, title: '10' },
                    { value: 25, title: '25' },
                    { value: 50, title: '50' },
                ],
                page: 1,
                sortby: [{ key: 'MA_Jobs.Job', order: 'asc' }],
            },
            planTableProps: {
                headers: [
                    { title: 'N° Piano',     align: 'start', value: 'ProductionPlanNo', key: 'MA_ProductionPlans.ProductionPlanNo', filterable: true, filtervalue: '' },
                    { title: 'Descrizione',  align: 'start', value: 'Description',      key: 'MA_ProductionPlans.Description',      filterable: true, filtervalue: '' },
                    { title: 'Data Crea.',   align: 'start', value: 'CreationDate',      key: 'MA_ProductionPlans.CreationDate',     filterable: true, filtervalue: '', date: true },
                ],
                items: [],
                total: 0,
                ipp: 10,
                ippo: [
                    { value: 10, title: '10' },
                    { value: 25, title: '25' },
                    { value: 50, title: '50' },
                ],
                page: 1,
                sortby: [{ key: 'MA_ProductionPlans.ProductionPlanNo', order: 'asc' }],
            },
            toast: {
                show: false,
                message: '',
                type: 'success',
            },
        };
    },
    computed: {
        createdRows() {
            return this.tableProps.items.filter(item => item.MOStatus == this.MagoEnums.Production_progress.Created);
        },
        allCreatedRowsSelected() {
            return this.createdRows.length > 0 && this.createdRows.every(item => this.selectedMos.includes(String(item.MONo)));
        },
        someCreatedRowsSelected() {
            return this.createdRows.some(item => this.selectedMos.includes(String(item.MONo)));
        },
        hasCreatedRows() {
            return this.createdRows.length > 0;
        },
        navigation() {
            return [
                { name: 'Dashboard', href: route('dashboard') },
                { name: this.$t('modules.gestione-produzione'), href: route('modules.home', 'gestione-produzione') },
                { name: this.$t('modules.resoconto-odp'), href: route('modules.gp.resoconto-odp') },
            ];
        },
    },
    mounted() {
        this.loadData();
    },
    methods: {
        toggleMoSelection(mono, selected) {
            const normalizedMono = String(mono);
            if (selected && !this.selectedMos.includes(normalizedMono)) {
                this.selectedMos.push(normalizedMono);
            } else if (!selected) {
                this.selectedMos = this.selectedMos.filter(selectedMono => selectedMono !== normalizedMono);
            }
        },
        toggleAllCreatedRows(selected) {
            const createdMos = this.createdRows.map(item => String(item.MONo));
            if (selected) {
                this.selectedMos = [...new Set([...this.selectedMos, ...createdMos])];
            } else {
                this.selectedMos = this.selectedMos.filter(mo => !createdMos.includes(mo));
                if (this.selectedMos.length) {
                    this.showToast(`Non tutte le righe sono state rimosse: restano ${this.selectedMos.length} ODP selezionati per il lancio.`, 'warning');
                }
            }
        },
        async launchSelectedMos() {
            const mos = [...new Set(this.selectedMos)];
            if (!mos.length || this.launchingSelectedMos) return;

            this.launchingSelectedMos = true;
            this.launchProgress = { completed: 0, total: mos.length };
            const launchedMos = [];
            const failedMos = [];

            try {
                for (const mo of mos) {
                    try {
                        const response = await axios.post(route('modules.gp.lanciaOdp'), { mo });
                        if (response.data?.success === false) {
                            failedMos.push({ mo, error: response.data.error || 'Errore durante il lancio' });
                        } else {
                            launchedMos.push(mo);
                        }
                    } catch (error) {
                        failedMos.push({
                            mo,
                            error: error.response?.data?.error || error.response?.data?.errorMessage || 'Errore durante il lancio',
                        });
                    } finally {
                        this.launchProgress.completed++;
                    }
                }

                this.selectedMos = this.selectedMos.filter(mo => !launchedMos.includes(mo));
                await this.loadData();

                if (failedMos.length) {
                    const firstFailure = failedMos[0];
                    this.showToast(
                        `${launchedMos.length} ODP lanciati; ${failedMos.length} non riusciti. ${firstFailure.mo}: ${firstFailure.error}`,
                        'error'
                    );
                } else {
                    this.showToast(`${launchedMos.length} ODP lanciati in produzione con successo`);
                }
            } finally {
                this.launchingSelectedMos = false;
            }
        },
        showToast(message, type = 'success') {
            this.toast.message = message;
            this.toast.type = type;
            this.toast.show = true;
        },
        formatDate(val) {
            if (!val) return '—';
            const d = new Date(val);
            if (isNaN(d)) return val;
            return d.toLocaleDateString('it-IT');
        },
        moStatusLabel(status) {
            const E = this.MagoEnums.Production_progress;
            const map = {
                [E.Released]:                'Lanciato',
                [E.Processing]:              'In lavorazione',
                [E.Confirmed]:               'Terminato',
                [E.Created]:                 'Creato',
                [E.Proposed_by_MRP]:         'Proposto da MRP',
                [E.Planned_by_CRP]:          'Pianificato da CRP',
                [E.Proposed_by_MRPMinusCRP]: 'Proposto MRP-CRP',
            };
            return map[status] ?? '—';
        },
        moStatusClass(status) {
            const E = this.MagoEnums.Production_progress;
            const map = {
                [E.Released]:                'bg-blue-100 text-blue-700',
                [E.Processing]:              'bg-yellow-100 text-yellow-700',
                [E.Confirmed]:               'bg-green-100 text-green-700',
                [E.Created]:                 'bg-gray-100 text-gray-600',
                [E.Proposed_by_MRP]:         'bg-purple-100 text-purple-700',
                [E.Planned_by_CRP]:          'bg-indigo-100 text-indigo-700',
                [E.Proposed_by_MRPMinusCRP]: 'bg-pink-100 text-pink-700',
            };
            return map[status] ?? 'bg-gray-100 text-gray-500';
        },
        async loadData(sortby, itemsperpage, page) {
            this.loading = true;
            try {
                if (sortby)       this.tableProps.sortby = sortby;
                if (itemsperpage) this.tableProps.ipp    = itemsperpage;
                if (page)         this.tableProps.page   = page;

                const res = await axios.post(route('modules.gp.resoconto-odp.data'), {
                    mono:             this.filters.mono,
                    job:              this.filters.job,
                    productionPlanNo: this.filters.productionPlanNo,
                    deliveryDateFrom: this.filters.deliveryDateFrom,
                    deliveryDateTo:   this.filters.deliveryDateTo,
                    statuses:         this.filters.statuses,
                    sortby:           Array.isArray(sortby) ? sortby : this.tableProps.sortby,
                    itemsperpage:     typeof itemsperpage === 'number' ? itemsperpage : this.tableProps.ipp,
                    page:             typeof page === 'number' ? page : this.tableProps.page,
                });

                if (res.data.success) {
                    this.tableProps.items  = res.data.items;
                    this.tableProps.total  = res.data.total;
                    this.tableProps.ipp    = res.data.ipp;
                    this.tableProps.page   = res.data.page;
                    this.tableProps.sortby = res.data.sortby || this.tableProps.sortby;
                } else {
                    this.showToast(res.data.errorMessage || 'Errore durante il caricamento', 'error');
                }
            } catch (e) {
                this.showToast(e.response?.data?.errorMessage || 'Errore durante il caricamento dei dati', 'error');
            } finally {
                this.loading = false;
            }
        },
        async loadJobs(sortby, itemsperpage, page) {
            this.showJobTable = true;

            if (sortby)       this.jobTableProps.sortby = sortby;
            if (itemsperpage) this.jobTableProps.ipp    = itemsperpage;
            if (page)         this.jobTableProps.page   = page;

            try {
                const res = await axios.post(route('modules.gp.resoconto-odp.jobs'), {
                    headers:      this.jobTableProps.headers,
                    sortby:       Array.isArray(sortby) ? sortby : this.jobTableProps.sortby,
                    itemsperpage: typeof itemsperpage === 'number' ? itemsperpage : this.jobTableProps.ipp,
                    page:         typeof page === 'number' ? page : this.jobTableProps.page,
                });

                if (res.data.success) {
                    this.jobTableProps.items   = res.data.jobs;
                    this.jobTableProps.total   = res.data.total;
                    this.jobTableProps.ipp     = res.data.ipp;
                    this.jobTableProps.page    = res.data.page;
                    this.jobTableProps.sortby  = res.data.sortby  || this.jobTableProps.sortby;
                    this.jobTableProps.headers = res.data.headers || this.jobTableProps.headers;
                }
            } catch (e) {
                this.showToast('Errore durante il caricamento delle commesse', 'error');
            }
        },
        selectJob(item) {
            this.filters.job = item.Job;
            this.showJobTable = false;
            this.loadData();
        },
        async loadPlans(sortby, itemsperpage, page) {
            this.showPlanTable = true;

            if (sortby)       this.planTableProps.sortby = sortby;
            if (itemsperpage) this.planTableProps.ipp    = itemsperpage;
            if (page)         this.planTableProps.page   = page;

            try {
                const res = await axios.post(route('modules.gp.resoconto-odp.plans'), {
                    headers:      this.planTableProps.headers,
                    sortby:       Array.isArray(sortby) ? sortby : this.planTableProps.sortby,
                    itemsperpage: typeof itemsperpage === 'number' ? itemsperpage : this.planTableProps.ipp,
                    page:         typeof page === 'number' ? page : this.planTableProps.page,
                });

                if (res.data.success) {
                    this.planTableProps.items   = res.data.plans;
                    this.planTableProps.total   = res.data.total;
                    this.planTableProps.ipp     = res.data.ipp;
                    this.planTableProps.page    = res.data.page;
                    this.planTableProps.sortby  = res.data.sortby  || this.planTableProps.sortby;
                    this.planTableProps.headers = res.data.headers || this.planTableProps.headers;
                }
            } catch (e) {
                this.showToast('Errore durante il caricamento dei piani di produzione', 'error');
            }
        },
        selectPlan(item) {
            this.filters.productionPlanNo = item.ProductionPlanNo;
            this.showPlanTable = false;
            this.loadData();
        },
        productionProgressPercent(item) {
            const produced = parseFloat(item.ProducedQty) || 0;
            const total    = parseFloat(item.ProductionQty) || 0;
            if (total === 0) return 0;
            return Math.round((produced / total) * 100);
        },
        productionProgressClass(item) {
            const pct = this.productionProgressPercent(item);
            if (pct >= 100) return 'bg-green-500';
            if (pct > 0)    return 'bg-blue-500';
            return 'bg-gray-300';
        },
        stepsProgressPercent(item) {
            const ratio = parseFloat(item.ProductionProgress);
            if (isNaN(ratio)) return 0;
            return Math.round(ratio * 100);
        },
        stepsProgressClass(item) {
            const pct = this.stepsProgressPercent(item);
            if (pct >= 100) return 'bg-green-500';
            if (pct > 0)    return 'bg-blue-500';
            return 'bg-gray-300';
        },
    },
};
</script>
